// PaymentReconciler — derived classification of Fakturownia payment allocations.
//
// Runs after each payments sync. For every non-deleted payment whose derived
// state could have changed (the DAO's listPaymentsNeedingClassification gate),
// it recomputes each allocation's state from CURRENT facts (payment content +
// current bank links) — classification is level-triggered, never accumulated
// (design §3). The only sticky state is an operator resolution pinned to the
// payment checksum: an allocation whose resolution is set AND whose
// resolution_checksum still equals the live checksum is left untouched.
//
// This class writes ONLY local read-model state (allocation states via the DAO,
// and — when it links a bank payment — that link's push status). It NEVER writes
// to `invoices` (the DB recompute trigger does that off allocation-state changes)
// and NEVER writes to Fakturownia.
//
// Deno-compatible: explicit .ts extensions, no Node-only APIs.

import type { FakturowniaPaymentDao } from './FakturowniaPaymentDao.ts';
import type { MbankPaymentLinkDao } from '../../mbank/dao/MbankPaymentLinkDao.ts';
import type { MbankStatementLineDao } from '../../mbank/dao/MbankStatementLineDao.ts';
import type { InvoiceDao } from '../../invoice/InvoiceDao.ts';
import type { CompanyDao } from '../../company/CompanyDao.ts';
import type { CompanyBankAccountDao } from '../../mbank/dao/CompanyBankAccountDao.ts';
import { isIbanShaped } from '../../mbank/matching/ibanShape.ts';
import type { FxRateService } from '../../fx/FxRateService.ts';
import type {
  FaPairingBasis,
  FaPairingStatus,
  FakturowniaPaymentAllocationRow,
  FakturowniaPaymentAllocationState,
  FakturowniaPaymentRow,
  FakturowniaPaymentState,
} from '@shared/types/fakturownia_payment.ts';
import type { InvoiceRow } from '@shared/types/invoice.ts';
import type { MbankPaymentLinkRow, MbankStatementLineRow } from '@shared/types/mbank.ts';
import { consoleErrorReporter, type ErrorReporter } from '../../reporter.ts';
import {
  faClientIdOf,
  isFaBankImport,
  pairFaImportPayment,
  type PairingResult,
} from './faImportPairing.ts';

/** Amount tolerance (ε) — mirrors the bank-link math and the exact-cover rule. */
const EPS = 0.01;

/** Dedupe token our pusher embeds in a payment name (`[mbank:<link_id>]`). The
 *  captured group is the originating mbank_payment_links id. Non-global so
 *  `.match()` stays stateless. Mirrors FakturowniaPaymentSyncService (that copy
 *  is module-private; kept in sync). */
const MBANK_LINK_TOKEN = /\bmbank:(\d+)\b/;

/** |a − b| in days; unparsable/missing dates sort as "infinitely far". */
function dateDistanceDays(a: string | null, b: string | null): number {
  if (!a || !b) return Number.MAX_SAFE_INTEGER;
  const ta = Date.parse(a);
  const tb = Date.parse(b);
  if (Number.isNaN(ta) || Number.isNaN(tb)) return Number.MAX_SAFE_INTEGER;
  return Math.abs(ta - tb) / 86_400_000;
}

/**
 * Order same-amount candidate links for pairing with an FA payment: smaller
 * |payment.paid_date − link.fx_rate_date| first (fx_rate_date is the
 * booking-derived settlement date — the closest bank-side analogue of the FA
 * paid date), then earliest created_at. Bank-transfer identity would beat both
 * signals, but accounting-entered FA payments don't carry one (D4).
 */
export function compareLinkCandidates(
  a: MbankPaymentLinkRow,
  b: MbankPaymentLinkRow,
  paymentPaidDate: string | null,
): number {
  const da = dateDistanceDays(paymentPaidDate, a.fx_rate_date);
  const db = dateDistanceDays(paymentPaidDate, b.fx_rate_date);
  if (da !== db) return da - db;
  return a.created_at <= b.created_at ? -1 : 1;
}

/** Extract the originating link id from a payment name's dedupe token. */
function extractMbankLinkId(name: string | null): number | null {
  if (name == null) return null;
  const m = name.match(MBANK_LINK_TOKEN);
  if (!m) return null;
  const n = Number(m[1]);
  return Number.isInteger(n) ? n : null;
}

/** One embedded invoice from a raw FA payment's `content.invoices`, validated. */
interface EmbeddedFaInvoice {
  /** FA invoice id as a string (allocations key on external_invoice_id strings). */
  id: string;
  /** |price_gross| — settlement math is magnitude-based, like the payment amount. */
  gross: number;
  currency: string | null;
}

/**
 * Parse the ORDERED embedded invoice list from a raw FA payment snapshot
 * (`fakturownia_payment.content.invoices`). FA settles a multi-invoice payment
 * sequentially in this array order (FIFO), so the order is load-bearing.
 * Returns null when the embed is unusable — absent, not an array, or ANY entry
 * lacking a numeric id / parseable price_gross (a partially-valid list would
 * corrupt the FIFO walk, so it's all-or-nothing).
 */
function parseEmbeddedInvoices(content: Record<string, unknown>): EmbeddedFaInvoice[] | null {
  const raw = (content as { invoices?: unknown }).invoices;
  if (!Array.isArray(raw) || raw.length === 0) return null;
  const out: EmbeddedFaInvoice[] = [];
  for (const entry of raw) {
    if (entry == null || typeof entry !== 'object') return null;
    const rec = entry as Record<string, unknown>;
    if (typeof rec.id !== 'number' || !Number.isFinite(rec.id)) return null;
    const rawGross = rec.price_gross;
    const gross =
      typeof rawGross === 'number'
        ? rawGross
        : typeof rawGross === 'string' && rawGross.trim() !== ''
          ? Number(rawGross)
          : NaN;
    if (!Number.isFinite(gross)) return null;
    out.push({
      id: String(rec.id),
      gross: Math.abs(gross),
      currency: typeof rec.currency === 'string' ? rec.currency : null,
    });
  }
  return out;
}

/**
 * Effective gross magnitude of an invoice folded with its corrections — the
 * same math as MbankManualMatchService.assertSplitsWithinOutstanding and the
 * DB trigger: a correction that over-reduces past zero (effective total flips
 * sign vs gross) leaves a credit, not a payable, so the magnitude collapses
 * to 0. Exported as the single TS-side definition — the resolution service
 * imports it rather than keeping another copy.
 */
export function effectiveGrossMagnitude(gross: number, corrDelta: number): number {
  const effective = gross + corrDelta;
  if (gross !== 0 && Math.sign(effective) !== Math.sign(gross)) return 0;
  return Math.abs(effective);
}

export interface ReconcileResult {
  paymentsProcessed: number;
  echo: number;
  linked: number;
  faOnly: number;
  conflict: number;
  orphan: number;
  /** Manual FA correction-netting payments auto-resolved (state
   *  'settled_by_correction'). Optional: only present when at least one fired. */
  settledByCorrection?: number;
}

/** Batched-run result — the plain rollup plus wall-clock duration. */
export type ReconcileBatchResult = ReconcileResult & { durationMs: number };

/**
 * Result of {@link PaymentReconciler.pairBatched} — the interim-mode
 * (payments_invoice_linking=false) payment-level pairing sweep. `processed`
 * counts every payment the batch attempted (successes and errors alike),
 * mirroring {@link ReconcileResult.paymentsProcessed}.
 */
export interface PairingBatchResult {
  processed: number;
  paired: number;
  manualException: number;
  noLine: number;
  ambiguous: number;
  errors: number;
}

/**
 * Prefetched reads for one {@link PaymentReconciler.pairBatched} sweep —
 * mirrors the {@link ReconcileBatchCtx} discipline but scoped to what the
 * pairing pass alone needs (no allocation / gross / link-adoption pools —
 * pairBatched never touches allocations or adopts links).
 */
interface PairingBatchCtx {
  /** mbank_push payments' own link, keyed by external_payment_id — echo_link
   *  basis: a push-originated FA payment pairs to its own link's statement
   *  line. Absent → 'mbank_push_without_link'. */
  linkByPushPaymentId: Map<string, MbankPaymentLinkRow>;
  /** fa-import candidate statement lines (amount+date superset), fetched
   *  exactly the way reconcileBatched's prefetch collects `pairingLines`.
   *  Empty when the batch holds no fa-import payments OR lineDao is unset. */
  pairingLines: MbankStatementLineRow[];
  /** FA client id → company id, for the client-id pairing fallback. */
  companyByFaClientId: Map<number, number>;
}

/**
 * Prefetched reads for a whole classification batch, replacing the per-payment
 * DAO round trips (kills the N+1). All maps are SUPERSETS keyed by invoice /
 * payment id; each payment reads only its own slice via `.get()`.
 */
interface ReconcileBatchCtx {
  /** listAllocationsByPaymentIds grouped by external_payment_id (id-ordered). */
  allocationsByPayment: Map<string, FakturowniaPaymentAllocationRow[]>;
  /** getGrossWithCorrectionDeltas over every family root in the batch. */
  gross: {
    grossById: Map<number, number>;
    corrDeltaByParent: Map<number, number>;
    corrIdToParent: Map<number, number>;
  };
  /** sumAppliedByInvoice over every family id (unfolded — folded per payment). */
  appliedByOwn: Map<number, number>;
  /** sumFaOnlyGroupedByInvoices — invoice → (payment → signed Σ) for self-exclusion. */
  faOnlyGrouped: Map<number, Map<string, number>>;
  /** findUnadoptedByInvoices bucketed by invoice_id — the shared candidate pool. */
  unadoptedByInvoice: Map<number, MbankPaymentLinkRow[]>;
  /** Links adopted during the batch — excluded from later payments' pools (mirrors
   *  the per-payment path's DB 'pushed' flip). */
  consumedLinkIds: Set<number>;
  /** fa-import pairing prefetch (empty when the batch holds no import payments):
   *  candidate statement lines by (amount, date) superset, their links, and the
   *  FA client-id → company map for the client fallback. */
  pairingLines: MbankStatementLineRow[];
  linksByLineId: Map<number, MbankPaymentLinkRow[]>;
  companyByFaClientId: Map<number, number>;
  /** match_alternatives written to lines during this batch — a later payment
   *  pairing to the same line merges on top of these, not the stale prefetch. */
  writtenAlternativesByLine: Map<number, unknown[]>;
}

/** Per-payment working state of the fa-import pass (pairing + line-scoped
 *  link pool + suggestions accumulated while classifying allocations). */
interface FaImportState {
  pairing: PairingResult;
  /** ALL links of the paired line (unfiltered — adoption filters inline). */
  lineLinks: MbankPaymentLinkRow[];
  /** Line links adopted for THIS payment's earlier allocations. */
  consumedLineLinkIds: Set<number>;
  /** FA pins to surface on the line as operator suggestions. */
  suggestions: { invoiceId: number; amount: number }[];
}

/**
 * Per-invoice fa_only Σ from OTHER payments (self excluded), reconstructed in
 * memory from the grouped totals — the batched equivalent of
 * `sumFaOnlyByInvoices(invoiceIds, excludePaymentId)`. Restricted to `invoiceIds`
 * so the result matches the per-payment query's scope exactly.
 */
function faOnlyOthers(
  grouped: Map<number, Map<string, number>>,
  excludeExternalPaymentId: string,
  invoiceIds: number[],
): Map<number, number> {
  const out = new Map<number, number>();
  for (const invoiceId of invoiceIds) {
    const byPayment = grouped.get(invoiceId);
    if (byPayment == null) continue;
    let sum = 0;
    let hasOther = false;
    for (const [paymentId, amount] of byPayment) {
      if (paymentId === excludeExternalPaymentId) continue;
      sum += amount;
      hasOther = true;
    }
    // Match the per-payment query: an invoice whose only fa_only rows are the
    // excluded payment's own returns NO row (absent from the map).
    if (hasOther) out.set(invoiceId, sum);
  }
  return out;
}

export class PaymentReconciler {
  constructor(
    private readonly dao: FakturowniaPaymentDao,
    private readonly linkDao: MbankPaymentLinkDao,
    private readonly invoiceDao: InvoiceDao,
    private readonly reporter: ErrorReporter = consoleErrorReporter,
    /** ccy→PLN rate resolution for the VAT-leg rule. Deliberately NO internal
     *  default: absent → the rule stays off and PLN-vs-FX payments keep the
     *  plain currency_mismatch conflict (fail-safe for callers that can't
     *  reach the DB rates table / NBP). */
    private readonly fx?: FxRateService,
    /** fa-import pass deps (statement-line pairing). Deliberately optional,
     *  mirroring the fx fail-safe: absent lineDao → the pass stays off and FA
     *  bank-import payments classify exactly as before. companyDao only powers
     *  the client-id pairing fallback — absent, the fallback never fires. */
    private readonly lineDao?: MbankStatementLineDao,
    private readonly companyDao?: CompanyDao,
    /** Trust-fold sink (T7 remediation 2026-08-03): when present, a freshly
     *  auto-paired DEBIT fa-import line also observes/promotes the client
     *  company's account and stamps the line's counterparty. Best-effort —
     *  absent or failing, classification is byte-identical. */
    private readonly companyBankDao?: CompanyBankAccountDao,
  ) {}

  async reconcile(limit = 200): Promise<ReconcileResult> {
    const result: ReconcileResult = {
      paymentsProcessed: 0,
      echo: 0,
      linked: 0,
      faOnly: 0,
      conflict: 0,
      orphan: 0,
    };

    const payments = await this.dao.listPaymentsNeedingClassification(limit);

    // Per-run cache of invoice rows (for the currency check) so a family shared
    // across payments in one sweep is loaded once.
    const invoiceCache = new Map<number, InvoiceRow>();
    const getInvoice = async (id: number): Promise<InvoiceRow> => {
      let inv = invoiceCache.get(id);
      if (inv === undefined) {
        inv = await this.invoiceDao.getById(id);
        invoiceCache.set(id, inv);
      }
      return inv;
    };

    for (const payment of payments) {
      // Per-payment error isolation: a poison payment is reported and skipped,
      // never stalling the rest of the batch.
      try {
        result.paymentsProcessed++;
        await this.reconcilePayment(payment, result, getInvoice);
      } catch (err) {
        this.reporter.captureException(err, {
          context: 'PaymentReconciler.reconcile',
          externalPaymentId: payment.external_payment_id,
        });
      }
    }

    return result;
  }

  /**
   * Batched reconcile — same classification as {@link reconcile} but every
   * per-payment DAO read (allocations, invoices, correction deltas, applied sums,
   * fa_only evidence, unadopted links) is hoisted into ONE bulk read for the whole
   * batch and threaded through the SHARED classification core via a
   * {@link ReconcileBatchCtx}. Per-payment writes (distinct match_detail) stay as
   * they are. Use this from the workflow-hosted sweep.
   */
  async reconcileBatched(limit = 200): Promise<ReconcileBatchResult> {
    const startedMs = Date.now();
    const result: ReconcileResult = {
      paymentsProcessed: 0,
      echo: 0,
      linked: 0,
      faOnly: 0,
      conflict: 0,
      orphan: 0,
    };

    const payments = await this.dao.listPaymentsNeedingClassification(limit);
    if (payments.length === 0) {
      return { ...result, durationMs: Date.now() - startedMs };
    }

    // ── Bulk prefetch (one read each, replacing the per-payment N+1) ─────────
    const paymentIds = payments.map((p) => p.external_payment_id);
    const allAllocs = await this.dao.listAllocationsByPaymentIds(paymentIds);
    const allocationsByPayment = new Map<string, FakturowniaPaymentAllocationRow[]>();
    for (const a of allAllocs) {
      const arr = allocationsByPayment.get(a.external_payment_id) ?? [];
      arr.push(a);
      allocationsByPayment.set(a.external_payment_id, arr);
    }

    // Every invoice any allocation points at (sticky rows included — a superset is
    // harmless; a resolved row still absent from `invoices` throws in getInvoice
    // and skips that one payment, exactly as the per-payment path does).
    const allInvoiceIds = [
      ...new Set(
        allAllocs.map((a) => a.invoice_id).filter((id): id is number => id != null),
      ),
    ];
    const invoiceMap = await this.dao.getInvoicesByIds(allInvoiceIds);

    // Family roots for every referenced invoice → one getGrossWithCorrectionDeltas.
    const rootSet = new Set<number>();
    for (const id of allInvoiceIds) {
      const inv = invoiceMap.get(id);
      const parent = (inv as { correction_of_invoice_id?: number | null } | undefined)
        ?.correction_of_invoice_id;
      rootSet.add(parent ?? id);
    }
    const allRootIds = [...rootSet];
    const gross = await this.invoiceDao.getGrossWithCorrectionDeltas(allRootIds);
    const allFamilyIds = [...new Set([...allRootIds, ...gross.corrIdToParent.keys()])];

    const appliedByOwn = await this.linkDao.sumAppliedByInvoice(allFamilyIds);
    const faOnlyGrouped = await this.dao.sumFaOnlyGroupedByInvoices(allFamilyIds);

    // Unadopted candidate pool — only when some payment is external (else the pool
    // is never consulted). Bucketed by invoice_id; consumption is tracked in memory.
    const hasExternal = payments.some((p) => p.origin === 'external');
    const unadoptedLinks = hasExternal
      ? await this.linkDao.findUnadoptedByInvoices(allFamilyIds)
      : [];
    const unadoptedByInvoice = new Map<number, MbankPaymentLinkRow[]>();
    for (const link of unadoptedLinks) {
      const arr = unadoptedByInvoice.get(link.invoice_id) ?? [];
      arr.push(link);
      unadoptedByInvoice.set(link.invoice_id, arr);
    }

    // fa-import pairing pool — only when the batch holds FA bank imports AND the
    // pass is on (lineDao wired). One superset read each; per-payment slices are
    // cut by pairFaImportPayment's own predicates.
    const importPayments = this.lineDao != null
      ? payments.filter((p) => p.origin === 'external' && isFaBankImport(p))
      : [];
    let pairingLines: MbankStatementLineRow[] = [];
    const linksByLineId = new Map<number, MbankPaymentLinkRow[]>();
    let companyByFaClientId = new Map<number, number>();
    if (importPayments.length > 0) {
      const amounts = [...new Set(importPayments.map((p) => Math.abs(p.amount)))];
      const dates = [
        ...new Set(importPayments.map((p) => p.paid_date).filter((d): d is string => d != null)),
      ];
      pairingLines = dates.length > 0
        ? await (this.lineDao as MbankStatementLineDao).findForPairing(amounts, dates)
        : [];
      if (pairingLines.length > 0) {
        const lineLinks = await this.linkDao.findByStatementLineIds(pairingLines.map((l) => l.id));
        for (const link of lineLinks) {
          const arr = linksByLineId.get(link.statement_line_id) ?? [];
          arr.push(link);
          linksByLineId.set(link.statement_line_id, arr);
        }
      }
      const clientIds = [
        ...new Set(importPayments.map(faClientIdOf).filter((c): c is number => c != null)),
      ];
      if (this.companyDao != null && clientIds.length > 0) {
        companyByFaClientId = await this.companyDao.getIdsByFakturowniaClientIds(clientIds);
      }
    }

    const ctx: ReconcileBatchCtx = {
      allocationsByPayment,
      gross,
      appliedByOwn,
      faOnlyGrouped,
      unadoptedByInvoice,
      consumedLinkIds: new Set<number>(),
      pairingLines,
      linksByLineId,
      companyByFaClientId,
      writtenAlternativesByLine: new Map<number, unknown[]>(),
    };

    // Invoice getter pre-warmed from the bulk load (misses fall back to getById).
    const invoiceCache = new Map<number, InvoiceRow>(invoiceMap);
    const getInvoice = async (id: number): Promise<InvoiceRow> => {
      let inv = invoiceCache.get(id);
      if (inv === undefined) {
        inv = await this.invoiceDao.getById(id);
        invoiceCache.set(id, inv);
      }
      return inv;
    };

    for (const payment of payments) {
      try {
        result.paymentsProcessed++;
        await this.reconcilePayment(payment, result, getInvoice, ctx);
      } catch (err) {
        this.reporter.captureException(err, {
          context: 'PaymentReconciler.reconcileBatched',
          externalPaymentId: payment.external_payment_id,
        });
      }
    }

    return { ...result, durationMs: Date.now() - startedMs };
  }

  /**
   * Interim-mode sweep (payments_invoice_linking=false): payment-level FA<->bank
   * pairing ONLY. Unlike {@link reconcileBatched}, this never touches
   * `fakturownia_payment.state` / `classified_checksum`, never writes an
   * allocation, never adopts a bank link (no `setPushStatus`), and never writes
   * a statement-line suggestion — those are all invoice-linking-mode concerns,
   * dormant here by design (design doc 2026-07-28). The pairing verdict is
   * ALWAYS re-stamped with the live checksum (drain-loop convergence), even for
   * `no_line`/`ambiguous` — a payment that never changes state would otherwise
   * spin forever on every sweep (permanently-pending precedent).
   *
   * Per-payment work is independent (no shared eviction state like
   * `reconcileBatched`'s `consumedLinkIds` — pairing never adopts a link), so
   * the batch runs concurrently in chunks of 25 rather than strictly serially.
   */
  async pairBatched(limit = 200): Promise<PairingBatchResult> {
    const result: PairingBatchResult = {
      processed: 0,
      paired: 0,
      manualException: 0,
      noLine: 0,
      ambiguous: 0,
      errors: 0,
    };

    const payments = await this.dao.listPaymentsNeedingPairing(limit);
    if (payments.length === 0) return result;

    // ── Bulk prefetch (one read each) ────────────────────────────────────────
    const pushIds = payments
      .filter((p) => p.origin === 'mbank_push')
      .map((p) => p.external_payment_id);
    const pushLinks = await this.linkDao.findByFakturowniaPaymentIds(pushIds);
    const linkByPushPaymentId = new Map<string, MbankPaymentLinkRow>();
    for (const link of pushLinks) {
      if (link.fakturownia_payment_id != null) {
        linkByPushPaymentId.set(link.fakturownia_payment_id, link);
      }
    }

    // fa-import candidate lines — only when the batch holds fa-import payments
    // AND the pass is on (lineDao wired), exactly like reconcileBatched's
    // `pairingLines` prefetch. Absent lineDao: every fa-import payment below
    // falls back to no_line/no_line_dao instead of querying.
    const importPayments = this.lineDao != null
      ? payments.filter((p) => p.origin === 'external' && isFaBankImport(p))
      : [];
    let pairingLines: MbankStatementLineRow[] = [];
    let companyByFaClientId = new Map<number, number>();
    if (importPayments.length > 0) {
      const amounts = [...new Set(importPayments.map((p) => Math.abs(p.amount)))];
      const dates = [
        ...new Set(importPayments.map((p) => p.paid_date).filter((d): d is string => d != null)),
      ];
      pairingLines = dates.length > 0
        ? await (this.lineDao as MbankStatementLineDao).findForPairing(amounts, dates)
        : [];
      const clientIds = [
        ...new Set(importPayments.map(faClientIdOf).filter((c): c is number => c != null)),
      ];
      if (this.companyDao != null && clientIds.length > 0) {
        companyByFaClientId = await this.companyDao.getIdsByFakturowniaClientIds(clientIds);
      }
    }

    const ctx: PairingBatchCtx = { linkByPushPaymentId, pairingLines, companyByFaClientId };

    // ── Per-payment classify + write, chunked concurrently ───────────────────
    const tasks = payments.map((payment) => async () => {
      try {
        result.processed++;
        await this.pairPayment(payment, ctx, result);
      } catch (err) {
        result.errors++;
        this.reporter.captureException(err, {
          context: 'PaymentReconciler.pairBatched',
          externalPaymentId: payment.external_payment_id,
        });
      }
    });
    for (let i = 0; i < tasks.length; i += 25) {
      await Promise.all(tasks.slice(i, i + 25).map((task) => task()));
    }

    return result;
  }

  /**
   * Re-open bank-import payments whose statement line arrived AFTER a sweep
   * judged them unpaired (pairing `no_line`, or a `conflict` /
   * `fa_import_unpaired` allocation). Both sweep gates fire on Fakturownia-side
   * change only, so without this a late statement upload is never noticed.
   *
   * Convergent by construction: a payment is re-opened only when the SAME pure
   * pairing the sweeps use ({@link pairFaImportPayment}, same line query) now
   * returns `paired` — still-unpaired and ambiguous payments are left alone,
   * as are operator-resolved ones. Once the sweeps rewrite the verdicts the
   * payment drops out of the candidate set. One bounded step; the workflow
   * calls it once before the reconcile loop.
   */
  async reopenArrivedLines(): Promise<{ checked: number; reopened: number }> {
    if (this.lineDao == null) return { checked: 0, reopened: 0 };
    const candidates = (await this.dao.listUnpairedBankImportPayments()).filter(
      (c) =>
        c.payment.origin === 'external' &&
        isFaBankImport(c.payment) &&
        c.payment.paid_date != null &&
        c.payment.pairing_resolution == null,
    );

    let reopened = 0;
    for (let i = 0; i < candidates.length; i += 200) {
      const chunk = candidates.slice(i, i + 200);
      const amounts = [...new Set(chunk.map((c) => Math.abs(c.payment.amount)))];
      const dates = [...new Set(chunk.map((c) => c.payment.paid_date as string))];
      const lines = await this.lineDao.findForPairing(amounts, dates);
      if (lines.length === 0) continue;

      const clientIds = [
        ...new Set(chunk.map((c) => faClientIdOf(c.payment)).filter((id): id is number => id != null)),
      ];
      const companyByFaClientId =
        this.companyDao != null && clientIds.length > 0
          ? await this.companyDao.getIdsByFakturowniaClientIds(clientIds)
          : new Map<number, number>();

      for (const c of chunk) {
        // An already-open pairing gate needs no write.
        const pairing = c.pairingNoLine && c.payment.pairing_checksum != null;
        const classification = c.classificationUnpaired;
        if (!pairing && !classification) continue;
        const clientId = faClientIdOf(c.payment);
        const clientCompanyId = clientId != null ? companyByFaClientId.get(clientId) ?? null : null;
        if (pairFaImportPayment(c.payment, lines, clientCompanyId).outcome !== 'paired') continue;
        await this.dao.reopenForSweeps(c.payment.external_payment_id, { pairing, classification });
        reopened++;
      }
    }
    return { checked: candidates.length, reopened };
  }

  /**
   * Classify + persist ONE payment's interim pairing verdict.
   *
   * (a) Sticky resolution pinned to the current checksum wins outright:
   *     `accepted_manual` → manual_exception with no line; `manual_pair` →
   *     paired/basis 'resolution' keeping the payment's current
   *     `paired_statement_line_id`. A resolution pinned to a STALE checksum is
   *     cleared (`setPairingResolution` nulled out) and falls through to (b)-(d)
   *     to recompute fresh.
   * (b) `mbank_push` → its own link's statement line (echo_link) or
   *     `no_line`/`mbank_push_without_link` when the payment has no link.
   * (c) FA bank-import (`isFaBankImport`) → `pairFaImportPayment`; `no_line_dao`
   *     when the pass is off (no lineDao wired).
   * (d) Everything else (kompensata / konsolidacja / manual entries) →
   *     `manual_exception`.
   * `setPairing` is ALWAYS the final write, stamping `pairing_checksum` to the
   * payment's live checksum regardless of outcome.
   */
  private async pairPayment(
    payment: FakturowniaPaymentRow,
    ctx: PairingBatchCtx,
    result: PairingBatchResult,
  ): Promise<void> {
    // (a) Sticky operator resolution.
    if (payment.pairing_resolution != null) {
      if (payment.pairing_resolution_checksum === payment.checksum) {
        if (payment.pairing_resolution === 'accepted_manual') {
          await this.dao.setPairing(payment.external_payment_id, {
            paired_statement_line_id: null,
            pairing_status: 'manual_exception',
            pairing_basis: null,
            pairing_checksum: payment.checksum,
          });
          result.manualException++;
        } else {
          // 'manual_pair' — keep the operator-chosen line.
          await this.dao.setPairing(payment.external_payment_id, {
            paired_statement_line_id: payment.paired_statement_line_id,
            pairing_status: 'paired',
            pairing_basis: 'resolution',
            pairing_checksum: payment.checksum,
          });
          result.paired++;
        }
        return;
      }
      // Stale — the resolution no longer applies to the live content; clear it
      // and recompute fresh below (same discipline as clearStaleResolutions).
      await this.dao.setPairingResolution(payment.external_payment_id, {
        pairing_resolution: null,
        pairing_resolved_by: null,
        pairing_resolved_at: null,
        pairing_resolution_checksum: null,
      });
    }

    // (b)-(d) Fresh classification.
    let status: FaPairingStatus;
    let basis: FaPairingBasis | null = null;
    let lineId: number | null = null;
    let detail: Record<string, unknown> | null = null;

    if (payment.origin === 'mbank_push') {
      const link = ctx.linkByPushPaymentId.get(payment.external_payment_id);
      if (link != null) {
        status = 'paired';
        basis = 'echo_link';
        lineId = link.statement_line_id;
      } else {
        status = 'no_line';
        detail = { reason: 'mbank_push_without_link' };
      }
    } else if (payment.origin === 'external' && isFaBankImport(payment)) {
      if (this.lineDao == null) {
        status = 'no_line';
        detail = { reason: 'no_line_dao' };
      } else {
        const clientId = faClientIdOf(payment);
        const clientCompanyId =
          clientId != null ? ctx.companyByFaClientId.get(clientId) ?? null : null;
        const pairing = pairFaImportPayment(payment, ctx.pairingLines, clientCompanyId);
        if (pairing.outcome === 'paired') {
          status = 'paired';
          basis = pairing.basis;
          lineId = pairing.line.id;
          // Trust fold (T7): the pairing proves this line belongs to the
          // payment's FA client. Best-effort — never affects classification.
          await this.foldPairedLineTrust(pairing.line, clientCompanyId);
        } else if (pairing.outcome === 'ambiguous') {
          status = 'ambiguous';
          detail = { candidateLineIds: pairing.candidateLineIds };
        } else {
          status = 'no_line';
        }
      }
    } else {
      // kompensata / konsolidacja / manual entries — no bank pairing signal.
      status = 'manual_exception';
    }

    await this.dao.setPairing(payment.external_payment_id, {
      paired_statement_line_id: lineId,
      pairing_status: status,
      pairing_basis: basis,
      pairing_checksum: payment.checksum,
      pairing_detail: detail,
    });

    switch (status) {
      case 'paired':
        result.paired++;
        break;
      case 'manual_exception':
        result.manualException++;
        break;
      case 'no_line':
        result.noLine++;
        break;
      case 'ambiguous':
        result.ambiguous++;
        break;
    }
  }

  /** Build a payment's candidate-link list from the shared batch pool, dropping
   *  already-consumed links and ordering oldest-first (mirrors the DB read). */
  private buildAvailableLinks(ctx: ReconcileBatchCtx, familyIds: number[]): MbankPaymentLinkRow[] {
    const seen = new Set<number>();
    const out: MbankPaymentLinkRow[] = [];
    for (const invoiceId of familyIds) {
      for (const link of ctx.unadoptedByInvoice.get(invoiceId) ?? []) {
        if (ctx.consumedLinkIds.has(link.id) || seen.has(link.id)) continue;
        seen.add(link.id);
        out.push(link);
      }
    }
    out.sort((a, b) => (a.created_at <= b.created_at ? -1 : 1));
    return out;
  }

  private async reconcilePayment(
    payment: FakturowniaPaymentRow,
    result: ReconcileResult,
    getInvoice: (id: number) => Promise<InvoiceRow>,
    ctx?: ReconcileBatchCtx,
  ): Promise<void> {
    const allocations = ctx
      ? (ctx.allocationsByPayment.get(payment.external_payment_id) ?? [])
      : await this.dao.listAllocations(payment.external_payment_id);

    // Zero allocations → the payment is pinned to no invoice at all.
    if (allocations.length === 0) {
      await this.dao.markPaymentClassified(payment.external_payment_id, 'orphan', payment.checksum);
      result.orphan++;
      return;
    }

    // Sticky partition: a resolution pinned to the CURRENT checksum is honored —
    // the allocation is left untouched and its (rejected → 'rejected', else
    // stored) state feeds the rollup. Everything else is (re)classified.
    const effectiveStates: FakturowniaPaymentAllocationState[] = [];
    const working: FakturowniaPaymentAllocationRow[] = [];
    for (const a of allocations) {
      const sticky = a.resolution != null && a.resolution_checksum === payment.checksum;
      if (sticky) {
        effectiveStates.push(a.resolution === 'rejected' ? 'rejected' : a.state);
      } else {
        working.push(a);
      }
    }

    // Unresolved (invoice_id still null) allocations stay 'pending' and are
    // retried next sweep once the invoice syncs.
    const resolved = working.filter((a) => a.invoice_id != null);
    const unresolvedCount = working.length - resolved.length;
    for (let i = 0; i < unresolvedCount; i++) effectiveStates.push('pending');

    if (resolved.length > 0) {
      await this.classifyResolved(payment, resolved, effectiveStates, result, getInvoice, ctx);
    }

    // Rollup: any conflict → conflict; any pending remaining → pending; else ok.
    const state: FakturowniaPaymentState = effectiveStates.includes('conflict')
      ? 'conflict'
      : effectiveStates.includes('pending')
        ? 'pending'
        : 'ok';
    await this.dao.markPaymentClassified(payment.external_payment_id, state, payment.checksum);
  }

  private async classifyResolved(
    payment: FakturowniaPaymentRow,
    resolved: FakturowniaPaymentAllocationRow[],
    effectiveStates: FakturowniaPaymentAllocationState[],
    result: ReconcileResult,
    getInvoice: (id: number) => Promise<InvoiceRow>,
    ctx?: ReconcileBatchCtx,
  ): Promise<void> {
    // Resolve each allocation's family ROOT first: an FA payment may be pinned to
    // a correction invoice (konsolidacja z korektą) — its evidence counts toward
    // the parent, mirroring recompute_invoice_payment_status's root anchoring.
    const rootByInvoice = new Map<number, number>();
    for (const a of resolved) {
      const invoiceId = a.invoice_id as number;
      const inv = await getInvoice(invoiceId);
      const parent = (inv as { correction_of_invoice_id?: number | null }).correction_of_invoice_id;
      rootByInvoice.set(invoiceId, parent ?? invoiceId);
    }
    const rootIds = [...new Set(rootByInvoice.values())];
    // Batch: the gross/correction maps were fetched once for the whole batch (they
    // are supersets keyed by id). Per-payment: fetch this payment's roots only.
    const { grossById, corrDeltaByParent, corrIdToParent } = ctx
      ? ctx.gross
      : await this.invoiceDao.getGrossWithCorrectionDeltas(rootIds);

    // Applied bank links across the whole family (root + corrections), corrections
    // folded onto their parent — identical to assertSplitsWithinOutstanding. In
    // batch mode family ids are derived from the GLOBAL correction map filtered to
    // this payment's roots (matching the per-payment getGross(rootIds) scope).
    const familyIds = ctx
      ? [
          ...new Set([
            ...rootIds,
            ...[...corrIdToParent]
              .filter(([, parent]) => rootIds.includes(parent))
              .map(([cid]) => cid),
          ]),
        ]
      : [...new Set([...rootIds, ...corrIdToParent.keys()])];
    const appliedByOwn = ctx ? ctx.appliedByOwn : await this.linkDao.sumAppliedByInvoice(familyIds);
    const appliedByInvoice = new Map<number, number>();
    for (const [own, amt] of appliedByOwn) {
      const target = corrIdToParent.get(own) ?? own;
      appliedByInvoice.set(target, (appliedByInvoice.get(target) ?? 0) + amt);
    }

    // Persisted fa_only evidence from OTHER payments (family-folded, this payment
    // excluded — its own stale allocations are re-derived below). Without this, a
    // duplicate FA entry split across two payments or two sweeps sails through the
    // over-payment guard (edge-case matrix #12). Batch: reconstruct "others" from
    // the grouped totals; per-payment: the excluded query.
    const faOnlyByOwn = ctx
      ? faOnlyOthers(ctx.faOnlyGrouped, payment.external_payment_id, familyIds)
      : await this.dao.sumFaOnlyByInvoices(familyIds, payment.external_payment_id);
    const faOnlyByRoot = new Map<number, number>();
    for (const [own, amt] of faOnlyByOwn) {
      const target = corrIdToParent.get(own) ?? own;
      faOnlyByRoot.set(target, (faOnlyByRoot.get(target) ?? 0) + amt);
    }

    const effGrossMag = (rootId: number): number =>
      effectiveGrossMagnitude(grossById.get(rootId) ?? 0, corrDeltaByParent.get(rootId) ?? 0);
    const outstandingOf = (rootId: number): number => {
      const applied = Math.max(0, appliedByInvoice.get(rootId) ?? 0);
      const persistedFaOnly = Math.max(0, faOnlyByRoot.get(rootId) ?? 0);
      return Math.round((effGrossMag(rootId) - applied - persistedFaOnly) * 100) / 100;
    };

    // ── Step 2: per-allocation amounts ──────────────────────────────────────
    // MAGNITUDE, not raw price: FA records outgoing payments (cost-invoice
    // settlements, income=false) with a NEGATIVE price. Settlement math is
    // direction-agnostic here — the invoice's own sign convention is folded by
    // effectiveGrossMagnitude, so a −4 007,34 outgoing payment settles a cost
    // invoice exactly like a +4 007,34 incoming one settles a sale.
    const paymentMagnitude = Math.abs(payment.amount);
    const allocatedById = new Map<number, number>();
    // Per-allocation amount provenance (e.g. the FIFO split basis) — merged into
    // whatever match_detail step 3 writes so the lineage survives classification.
    const detailById = new Map<number, Record<string, unknown>>();
    if (resolved.length === 1) {
      allocatedById.set(resolved[0].id, paymentMagnitude);
    } else {
      // Multi-invoice: exact-cover — payment total == Σ outstanding (each fully
      // covered, ±0.01/invoice). Otherwise the split is ambiguous → all conflict.
      const n = resolved.length;
      const outstandingByInvoice: Record<number, number> = {};
      let sumOutstanding = 0;
      for (const a of resolved) {
        const o = outstandingOf(rootByInvoice.get(a.invoice_id as number) as number);
        outstandingByInvoice[a.invoice_id as number] = o;
        sumOutstanding += o;
      }
      if (Math.abs(paymentMagnitude - sumOutstanding) <= EPS * n) {
        for (const a of resolved) {
          allocatedById.set(a.id, outstandingByInvoice[a.invoice_id as number]);
        }
      } else {
        // Exact-cover failed against OUR outstanding (corrections/partials skew
        // it) — before conceding, try FA's own documented semantics: the
        // embedded invoice list is settled sequentially (FIFO) in array order.
        const fifo = this.tryFifoSplit(payment, resolved, paymentMagnitude);
        if (fifo != null) {
          for (const a of resolved) {
            const f = fifo.get(a.id) as { amount: number; faGross: number; fifoPosition: number };
            allocatedById.set(a.id, f.amount);
            detailById.set(a.id, {
              allocBasis: 'fa_fifo',
              faGross: f.faGross,
              fifoPosition: f.fifoPosition,
            });
          }
        } else {
          const detail = {
            reason: 'unsplittable_multi_invoice',
            paymentAmount: paymentMagnitude,
            outstandingByInvoice,
          };
          for (const a of resolved) {
            await this.dao.setAllocationState(a.id, 'conflict', { match_detail: detail });
            result.conflict++;
            effectiveStates.push('conflict');
          }
          return; // unsplittable — no per-allocation classification
        }
      }
    }

    // ── Step 2b: fa-import pairing ──────────────────────────────────────────
    // FA's own mBank feed echoed back (content.provider='mbank', kind='import')
    // is the SAME physical transfer as one of our statement lines — never
    // independent evidence. Pair it once per payment; the per-allocation loop
    // then short-circuits steps (b)–(e) for these payments. Dep-gated: without
    // lineDao the pass is off and the payment flows through the legacy chain.
    const faImport =
      this.lineDao != null && payment.origin === 'external' && isFaBankImport(payment)
        ? await this.resolveFaImportPairing(payment, ctx)
        : null;

    // ── Step 3: per-allocation classification ───────────────────────────────
    // Candidate bank links (external payments only), consumed as they're adopted
    // so one link can't explain two allocations. fa-import payments never touch
    // the family-wide pool — their adoption is line-scoped (step (a3)).
    const availableLinks =
      payment.origin === 'external' && faImport == null
        ? ctx
          ? this.buildAvailableLinks(ctx, familyIds)
          : [...(await this.linkDao.findUnadoptedByInvoices(familyIds))]
        : [];
    // fa_only amounts assigned to THIS payment's allocations during this pass,
    // keyed by family ROOT — combined with the persisted fa_only of other
    // payments (faOnlyByRoot) in the over-payment check so duplicates are caught
    // within one payment, across payments, and across sweeps alike.
    const runFaOnly = new Map<number, number>();

    for (const a of resolved) {
      const invoiceId = a.invoice_id as number;
      const amt = allocatedById.get(a.id) ?? paymentMagnitude;
      const baseDetail = detailById.get(a.id);
      const now = new Date().toISOString();

      // (a) Our own push (token) — echo when it still points at its link's
      //     invoice with the link's amount; else echo_drift (edit or re-pin).
      if (payment.origin === 'mbank_push') {
        const tokenLinkId = extractMbankLinkId(payment.name);
        const link = tokenLinkId != null ? (await this.linkDao.findByIds([tokenLinkId]))[0] ?? null : null;
        const sameInvoice = link != null && link.invoice_id === invoiceId;
        const amountMatch =
          link != null && Math.abs(Math.abs(amt) - Math.abs(link.applied_in_invoice_ccy)) <= EPS;
        if (sameInvoice && amountMatch) {
          await this.dao.setAllocationState(a.id, 'echo', {
            allocated_amount: amt,
            mbank_payment_link_id: link.id,
          });
          result.echo++;
          effectiveStates.push('echo');
        } else {
          await this.dao.setAllocationState(a.id, 'conflict', {
            allocated_amount: amt,
            match_detail: {
              reason: 'echo_drift',
              linkInvoiceId: link?.invoice_id ?? null,
              linkAmount: link?.applied_in_invoice_ccy ?? null,
            },
          });
          result.conflict++;
          effectiveStates.push('conflict');
        }
        continue;
      }

      // (a2) Previously-adopted external allocation — a bank link already owns
      //      this money (adopted by (c) below in an earlier sweep, or by the
      //      pusher's adopt-before-POST). Re-derivation must NOT re-run the
      //      settlement math: the link's amount is already inside
      //      alreadyApplied, so falling through would count the same money
      //      TWICE and flag a phantom overpayment (observed on dev 2026-07-20:
      //      a full re-derive flipped all 154 adopted allocations to conflict).
      //      Verify the pairing like an echo and keep/restore 'linked'; a
      //      vanished or re-pinned link drifts to conflict for operator eyes.
      if (a.mbank_payment_link_id != null) {
        const link = (await this.linkDao.findByIds([a.mbank_payment_link_id]))[0] ?? null;
        const sameInvoice = link != null && link.invoice_id === invoiceId;
        const amountMatch =
          link != null && Math.abs(Math.abs(amt) - Math.abs(link.applied_in_invoice_ccy)) <= EPS;
        if (sameInvoice && amountMatch) {
          await this.dao.setAllocationState(a.id, 'linked', {
            allocated_amount: amt,
            match_detail: { ...(baseDetail ?? {}), linkBasis: 'adopted_link' },
          });
          result.linked++;
          effectiveStates.push('linked');
        } else {
          await this.dao.setAllocationState(a.id, 'conflict', {
            allocated_amount: amt,
            match_detail: {
              ...(baseDetail ?? {}),
              reason: 'echo_drift',
              linkInvoiceId: link?.invoice_id ?? null,
              linkAmount: link?.applied_in_invoice_ccy ?? null,
            },
          });
          result.conflict++;
          effectiveStates.push('conflict');
        }
        continue;
      }

      // (a3) FA bank import — see Step 2b. Paired: the line's own links explain
      //      the money ('linked') or the allocation is a pure echo (no coverage
      //      — the DB trigger counts only fa_only; the bank statement stays the
      //      sole source of truth). Unpairable: operator review via the
      //      conflict queue (user decision 2026-07-24: BOTH no-line and
      //      ambiguous-line shapes go to review).
      if (faImport != null) {
        if (faImport.pairing.outcome !== 'paired') {
          await this.dao.setAllocationState(a.id, 'conflict', {
            allocated_amount: amt,
            match_detail: {
              ...(baseDetail ?? {}),
              reason: 'fa_import_unpaired',
              pairing: faImport.pairing.outcome === 'no_line' ? 'no_line' : 'ambiguous_lines',
              ...(faImport.pairing.outcome === 'ambiguous'
                ? { candidateLineIds: faImport.pairing.candidateLineIds }
                : {}),
            },
          });
          result.conflict++;
          effectiveStates.push('conflict');
          continue;
        }
        const line = faImport.pairing.line;
        const pairingBasis = faImport.pairing.basis;
        // Line-scoped adoption: an unadopted link OF THIS LINE on the same
        // invoice with the allocation's amount is the transfer's own booking.
        // Adoption is PAYMENT-level. The payment already matched this line on
        // amount + paid_date + title, so a link on that line for that invoice is
        // the same money by construction — a few grosze between FA's figure and
        // ours (rounding, or FA allocating a batch slightly differently) must not
        // break the pairing. Requiring per-allocation equality stranded real
        // payments as "Poza Fakturownią" (prod invoice 555: FA 16 550,78 vs link
        // 16 550,76). Invoice identity is still required; when several links on
        // the line share the invoice, the closest amount wins.
        const adoptable = faImport.lineLinks.filter(
          (l) =>
            !faImport.consumedLineLinkIds.has(l.id) &&
            !(ctx?.consumedLinkIds.has(l.id) ?? false) &&
            l.fakturownia_payment_id == null &&
            l.fakturownia_push_status !== 'pushed' &&
            l.fakturownia_push_status !== 'pushing' &&
            l.invoice_id === invoiceId,
        );
        const lineLink =
          adoptable.length === 0
            ? undefined
            : adoptable.reduce((best, l) =>
                Math.abs(Math.abs(l.applied_in_invoice_ccy) - Math.abs(amt)) <
                Math.abs(Math.abs(best.applied_in_invoice_ccy) - Math.abs(amt))
                  ? l
                  : best,
              );
        if (lineLink != null) {
          faImport.consumedLineLinkIds.add(lineLink.id);
          if (ctx) ctx.consumedLinkIds.add(lineLink.id);
          await this.linkDao.setPushStatus(lineLink.id, 'pushed', {
            fakturownia_payment_id: payment.external_payment_id,
            fakturownia_pushed_at: now,
          });
          await this.dao.setAllocationState(a.id, 'linked', {
            allocated_amount: amt,
            mbank_payment_link_id: lineLink.id,
            match_detail: { ...(baseDetail ?? {}), linkBasis: 'fa_import_line' },
          });
          result.linked++;
          effectiveStates.push('linked');
        } else {
          await this.dao.setAllocationState(a.id, 'echo', {
            allocated_amount: amt,
            mbank_payment_link_id: null,
            match_detail: {
              ...(baseDetail ?? {}),
              reason: 'fa_import_echo',
              statementLineId: line.id,
              pairingBasis,
            },
          });
          result.echo++;
          effectiveStates.push('echo');
          // FA's pin becomes an operator suggestion for the line — only with
          // a positive amount and agreeing currencies (no FX guessing).
          if (amt > 0) {
            const invoice = await getInvoice(invoiceId);
            const invCcy = invoice.currency ?? null;
            if (invCcy == null || invCcy === line.currency) {
              faImport.suggestions.push({ invoiceId, amount: Math.abs(amt) });
            }
          }
        }
        continue;
      }

      // FIFO tail: an embedded invoice fully covered by the ones before it
      // takes 0 — pure FA-side "nothing applied here" evidence. No link /
      // over-payment math is meaningful on a zero amount.
      if (amt === 0 && baseDetail != null) {
        await this.dao.setAllocationState(a.id, 'fa_only', {
          allocated_amount: 0,
          mbank_payment_link_id: null,
          match_detail: baseDetail,
        });
        result.faOnly++;
        effectiveStates.push('fa_only');
        continue;
      }

      // (b) External — currency mismatch (no FX guessing on FA-side rows),
      //     EXCEPT the split-payment VAT-leg shape: a PLN payment that settles
      //     exactly the PLN VAT of a single FX invoice (net paid separately in
      //     the invoice currency). That one is verifiable against a rate.
      if (payment.currency != null) {
        const invoice = await getInvoice(invoiceId);
        const invCcy = invoice.currency ?? null;
        if (invCcy != null && payment.currency !== invCcy) {
          if (payment.currency === 'PLN' && resolved.length === 1) {
            const handled = await this.classifyVatLeg(
              payment, a, invoice, paymentMagnitude, result, effectiveStates,
            );
            if (handled) continue;
          }
          await this.dao.setAllocationState(a.id, 'conflict', {
            allocated_amount: amt,
            match_detail: {
              ...(baseDetail ?? {}),
              reason: 'currency_mismatch',
              paymentCurrency: payment.currency,
              invoiceCurrency: invCcy,
            },
          });
          result.conflict++;
          effectiveStates.push('conflict');
          continue;
        }
      }

      // (c) Linked — an un-adopted bank link on the family with matching amount.
      //     When several links match by amount (recurring same-amount transfers),
      //     the one whose settlement date (fx_rate_date — booking-derived) is
      //     closest to the FA payment's paid_date wins; created_at breaks the
      //     remaining tie. The pairing basis is recorded in match_detail so the
      //     lineage is auditable (bank identity itself isn't available on
      //     accounting-entered FA payments — see D4).
      const matches = availableLinks.filter(
        (l) => Math.abs(Math.abs(amt) - Math.abs(l.applied_in_invoice_ccy)) <= EPS,
      );
      if (matches.length > 0) {
        const chosen = matches.reduce((x, y) =>
          compareLinkCandidates(x, y, payment.paid_date) <= 0 ? x : y,
        );
        const idx = availableLinks.indexOf(chosen);
        if (idx >= 0) availableLinks.splice(idx, 1);
        // Batch: also drop it from the shared pool so a later payment in the same
        // sweep can't re-adopt it (the per-payment path relies on the DB 'pushed'
        // flip below for the same effect across its fresh reads).
        if (ctx) ctx.consumedLinkIds.add(chosen.id);
        await this.linkDao.setPushStatus(chosen.id, 'pushed', {
          fakturownia_payment_id: payment.external_payment_id,
          fakturownia_pushed_at: now,
        });
        await this.dao.setAllocationState(a.id, 'linked', {
          allocated_amount: amt,
          mbank_payment_link_id: chosen.id,
          match_detail: {
            ...(baseDetail ?? {}),
            linkBasis: matches.length > 1 ? 'amount+closest_date' : 'amount',
            linkDate: chosen.fx_rate_date,
            paymentPaidDate: payment.paid_date,
            candidateCount: matches.length,
          },
        });
        result.linked++;
        effectiveStates.push('linked');
        continue;
      }

      // (d) Over-payment — family settled (bank links + persisted fa_only of other
      //     payments + this payment's fa_only so far + this allocation) exceeds
      //     effective gross. All terms are family-root-anchored.
      const root = rootByInvoice.get(invoiceId) as number;
      const alreadyApplied = Math.max(0, appliedByInvoice.get(root) ?? 0);
      const persistedFaOnly = Math.max(0, faOnlyByRoot.get(root) ?? 0);
      const priorFaOnly = runFaOnly.get(root) ?? 0;
      const familySettled = alreadyApplied + persistedFaOnly + priorFaOnly + Math.abs(amt);
      if (familySettled > effGrossMag(root) + EPS) {
        // (d0) Correction netting — operators close an original invoice against
        //      its correction by entering a manual kompensata/konsolidacja in
        //      FA (FA's per-document ledger needs it; our effective-gross math
        //      does not). When such a payment's amount equals the family's
        //      correction delta, counting it would represent the SAME netting
        //      twice — it is redundant by design, not an operator problem.
        //      Derive 'settled_by_correction': no money counted (the trigger
        //      counts only fa_only), no conflict raised, re-derived each sweep.
        const corrDelta = Math.abs(corrDeltaByParent.get(root) ?? 0);
        const manualNetting =
          payment.settlement_class === 'compensation' ||
          payment.settlement_class === 'correction_consolidation';
        if (manualNetting && corrDelta > EPS && Math.abs(Math.abs(amt) - corrDelta) <= EPS) {
          await this.dao.setAllocationState(a.id, 'settled_by_correction', {
            allocated_amount: amt,
            mbank_payment_link_id: null,
            match_detail: {
              ...(baseDetail ?? {}),
              reason: 'settled_by_correction',
              correctionDelta: corrDelta,
              effectiveGross: effGrossMag(root),
              alreadyApplied,
              persistedFaOnly,
              allocatedAmount: Math.abs(amt),
            },
          });
          result.settledByCorrection = (result.settledByCorrection ?? 0) + 1;
          effectiveStates.push('settled_by_correction');
          continue;
        }
        await this.dao.setAllocationState(a.id, 'conflict', {
          allocated_amount: amt,
          match_detail: {
            ...(baseDetail ?? {}),
            reason: 'overpayment',
            effectiveGross: effGrossMag(root),
            alreadyApplied,
            persistedFaOnly,
            allocatedAmount: Math.abs(amt),
          },
        });
        result.conflict++;
        effectiveStates.push('conflict');
        continue;
      }

      // (e) FA-only evidence — counts toward payment_status (design D1).
      runFaOnly.set(root, priorFaOnly + Math.abs(amt));
      await this.dao.setAllocationState(a.id, 'fa_only', {
        allocated_amount: amt,
        mbank_payment_link_id: null,
        ...(baseDetail != null ? { match_detail: baseDetail } : {}),
      });
      result.faOnly++;
      effectiveStates.push('fa_only');
    }

    if (faImport != null && faImport.pairing.outcome === 'paired') {
      await this.writeFaImportSuggestions(payment, faImport.pairing.line, faImport, ctx);
    }
  }

  /** Fetch the fa-import pairing inputs for one payment (batch: slice the
   *  prefetched pools) and run the pure pairing. */
  private async resolveFaImportPairing(
    payment: FakturowniaPaymentRow,
    ctx?: ReconcileBatchCtx,
  ): Promise<FaImportState> {
    const lineDao = this.lineDao as MbankStatementLineDao;
    const lines = ctx
      ? ctx.pairingLines
      : payment.paid_date != null
        ? await lineDao.findForPairing([Math.abs(payment.amount)], [payment.paid_date])
        : [];

    const clientId = faClientIdOf(payment);
    let clientCompanyId: number | null = null;
    if (clientId != null) {
      if (ctx) {
        clientCompanyId = ctx.companyByFaClientId.get(clientId) ?? null;
      } else if (this.companyDao != null) {
        clientCompanyId =
          (await this.companyDao.getIdsByFakturowniaClientIds([clientId])).get(clientId) ?? null;
      }
    }

    const pairing = pairFaImportPayment(payment, lines, clientCompanyId);
    const lineLinks =
      pairing.outcome === 'paired'
        ? ctx
          ? (ctx.linksByLineId.get(pairing.line.id) ?? [])
          : await this.linkDao.findByStatementLineIds([pairing.line.id])
        : [];
    return { pairing, lineLinks, consumedLineLinkIds: new Set<number>(), suggestions: [] };
  }

  /**
   * Best-effort trust fold for a freshly auto-paired fa-import line (T7
   * remediation 2026-08-03): the pairing proves the line belongs to the
   * payment's FA client, so stamp the line's counterparty company, and for
   * settled DEBIT evidence observe + promote the client's account — the
   * live replacement for the one-shot backfill while interim mode keeps the
   * invoice matcher dormant. Same guards as every other learning path: no
   * reversals, IBAN-shaped only. Failures are swallowed; classification and
   * checksum flow are never affected.
   */
  private async foldPairedLineTrust(
    line: MbankStatementLineRow,
    clientCompanyId: number | null,
  ): Promise<void> {
    if (clientCompanyId == null) return;
    try {
      if (this.lineDao != null && line.counterparty_company_id == null) {
        await this.lineDao.setCounterpartyCompany(line.id, clientCompanyId);
      }
      if (
        this.companyBankDao != null &&
        line.direction === 'debit' &&
        !line.is_reversal &&
        isIbanShaped(line.counterparty_iban)
      ) {
        const iban = line.counterparty_iban as string;
        await this.companyBankDao.observe(
          clientCompanyId,
          iban,
          line.currency,
          { swiftCode: line.counterparty_bic ?? null },
          'observed_match',
        );
        await this.companyBankDao.promoteToPaymentVerified(clientCompanyId, iban);
      }
    } catch {
      // Best-effort by contract.
    }
  }

  /**
   * Surface the payment's FA pins on its paired line as operator suggestions
   * (match_alternatives entries with source='fa_import'), and promote a
   * pending line to 'ambiguous' so the alternatives card shows. Idempotent:
   * this payment's previous entries are replaced, other sources' (and other
   * payments') entries are kept. Lines already fully matched are left alone;
   * a partially_matched line gains entries but keeps its state and reason.
   */
  private async writeFaImportSuggestions(
    payment: FakturowniaPaymentRow,
    line: MbankStatementLineRow,
    faImport: FaImportState,
    ctx?: ReconcileBatchCtx,
  ): Promise<void> {
    const needsMatch =
      line.match_state === 'pending' ||
      line.match_state === 'ambiguous' ||
      line.match_state === 'partially_matched';
    if (!needsMatch) return;

    const base =
      ctx?.writtenAlternativesByLine.get(line.id) ??
      (Array.isArray(line.match_alternatives) ? (line.match_alternatives as unknown[]) : []);
    const kept = base.filter((alt) => {
      const rec = alt as { source?: unknown; externalPaymentId?: unknown } | null;
      return !(
        rec != null &&
        typeof rec === 'object' &&
        rec.source === 'fa_import' &&
        rec.externalPaymentId === payment.external_payment_id
      );
    });
    const fresh = faImport.suggestions.map((s) => ({
      source: 'fa_import',
      externalPaymentId: payment.external_payment_id,
      applications: [
        {
          invoiceId: s.invoiceId,
          appliedAmount: s.amount,
          appliedInInvoiceCcy: s.amount,
          fxRate: 1,
          fxRateSource: 'same',
          fxRateDate: line.booking_date,
          role: 'principal',
          matchRule: 'invoice_number_in_text',
        },
      ],
      evidence: {
        identitySignals: ['fa_import_pin'],
        amountSignal: 'amount_exact',
        temporalSignal: 'date_in_window',
        identityScore: 0.9,
        amountScore: 1,
        temporalScore: 1,
        totalScore: 0.9,
        runnerUpScore: 0,
        gap: 0.9,
      },
    }));
    // Nothing new and nothing stale to clean → skip the write entirely.
    if (fresh.length === 0 && kept.length === base.length) return;

    const merged = [...kept, ...fresh];
    await (this.lineDao as MbankStatementLineDao).updateFaImportAlternatives(line.id, merged, {
      promoteToAmbiguous: line.match_state === 'pending' && fresh.length > 0,
    });
    if (ctx) ctx.writtenAlternativesByLine.set(line.id, merged);
  }

  /**
   * FIFO auto-split of a multi-invoice payment from FA's OWN embedded invoice
   * list (`content.invoices`) — FA's documented semantics: invoices are settled
   * sequentially in array order; a short payment covers early invoices fully
   * and the last touched one partially. Used only after exact-cover against our
   * outstanding failed (corrections/partials skew our side; FA's grosses are
   * the authoritative split).
   *
   * Preconditions (else null → the caller keeps the unsplittable conflict):
   * - the embed parses in full (numeric ids + parseable grosses);
   * - every embedded invoice is in the payment's own currency (no FX guessing);
   * - every resolved allocation maps onto an embedded invoice by external id;
   * - the walk consumes the ENTIRE payment (payment ≤ Σ grosses ± EPS) — an
   *   overrun means the embed can't explain the amount.
   *
   * The walk runs over the FULL embedded list (an embedded invoice without a
   * resolved allocation — sticky or still unresolved — consumes its share too);
   * only resolved allocations get amounts assigned.
   */
  private tryFifoSplit(
    payment: FakturowniaPaymentRow,
    resolved: FakturowniaPaymentAllocationRow[],
    paymentMagnitude: number,
  ): Map<number, { amount: number; faGross: number; fifoPosition: number }> | null {
    const embedded = parseEmbeddedInvoices(payment.content);
    if (embedded == null) return null;
    for (const e of embedded) {
      if ((e.currency ?? null) !== (payment.currency ?? null)) return null;
    }
    const slotByExternalId = new Map<string, { index: number; gross: number }>();
    embedded.forEach((e, index) => {
      if (!slotByExternalId.has(e.id)) slotByExternalId.set(e.id, { index, gross: e.gross });
    });
    for (const a of resolved) {
      if (!slotByExternalId.has(String(a.external_invoice_id))) return null;
    }

    const takeByIndex: number[] = [];
    let remaining = paymentMagnitude;
    for (const e of embedded) {
      const take = Math.min(remaining, e.gross);
      takeByIndex.push(Math.round(take * 100) / 100);
      remaining = Math.round((remaining - take) * 100) / 100;
    }
    if (remaining > EPS) return null; // overrun: payment > Σ embedded grosses

    const out = new Map<number, { amount: number; faGross: number; fifoPosition: number }>();
    for (const a of resolved) {
      const slot = slotByExternalId.get(String(a.external_invoice_id)) as { index: number; gross: number };
      out.set(a.id, {
        amount: takeByIndex[slot.index],
        faGross: slot.gross,
        fifoPosition: slot.index,
      });
    }
    return out;
  }

  /**
   * VAT-leg rule for Polish split-payment FX invoices: the net travels in the
   * invoice currency (separate payment/leg) while the VAT is paid in PLN. A
   * single-allocation PLN payment on a non-PLN invoice whose amount matches
   * |vat_amount| × rate is that VAT leg — counted as fa_only evidence in the
   * INVOICE currency (|vat_amount|), so net-leg + VAT-leg together settle the
   * gross. A rate-verified amount that does NOT match is the audit surface for
   * "VAT paid incorrectly" → conflict `vat_leg_rate_mismatch`.
   *
   * Rate ladder: invoice.exchange_rate when usable, else FxRateService for
   * sell_date ?? issue_date (last-working-day-before semantics inside).
   *
   * Returns true when it classified the allocation; false → the caller falls
   * back to the plain currency_mismatch conflict (fail-safe: vat_amount
   * missing/zero, no usable rate source, or the rate lookup failing).
   */
  private async classifyVatLeg(
    payment: FakturowniaPaymentRow,
    allocation: FakturowniaPaymentAllocationRow,
    invoice: InvoiceRow,
    paymentMagnitude: number,
    result: ReconcileResult,
    effectiveStates: FakturowniaPaymentAllocationState[],
  ): Promise<boolean> {
    const vatAmount = Math.abs(invoice.vat_amount ?? 0);
    if (!(vatAmount > 0)) return false;

    let rate: number;
    let rateSource: string;
    const invoiceRate = invoice.exchange_rate;
    if (typeof invoiceRate === 'number' && Number.isFinite(invoiceRate) && invoiceRate > 0) {
      rate = invoiceRate;
      rateSource = 'invoice';
    } else {
      const anchorDate = invoice.sell_date ?? invoice.issue_date;
      if (this.fx == null || anchorDate == null) return false;
      try {
        const resolvedRate = await this.fx.rateToPln(invoice.currency, anchorDate);
        rate = resolvedRate.rate;
        rateSource = resolvedRate.rateSource;
      } catch (err) {
        // NBP/DB unreachable → keep today's currency_mismatch conflict, but
        // surface the degraded lookup.
        this.reporter.captureException(err, {
          context: 'PaymentReconciler.classifyVatLeg',
          externalPaymentId: payment.external_payment_id,
          invoiceId: invoice.id,
        });
        return false;
      }
    }

    const expectedPln = Math.round(vatAmount * rate * 100) / 100;
    const detail = {
      vatLeg: true,
      rate,
      rateSource,
      plnAmount: paymentMagnitude,
      expectedPln,
    };
    if (Math.abs(paymentMagnitude - expectedPln) <= Math.max(0.05, expectedPln * 0.005)) {
      await this.dao.setAllocationState(allocation.id, 'fa_only', {
        allocated_amount: vatAmount,
        mbank_payment_link_id: null,
        match_detail: detail,
      });
      result.faOnly++;
      effectiveStates.push('fa_only');
    } else {
      await this.dao.setAllocationState(allocation.id, 'conflict', {
        allocated_amount: paymentMagnitude,
        match_detail: { ...detail, reason: 'vat_leg_rate_mismatch' },
      });
      result.conflict++;
      effectiveStates.push('conflict');
    }
    return true;
  }
}
